import React, { useState } from 'react';
import { Smartphone, Zap, Tv, Wifi, Receipt, History, Bookmark, CheckCircle2 } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Modal } from '../components/ui/Modal';
import { EmptyState } from '../components/ui/EmptyState';
import { toast } from 'sonner';
type Category = 'Airtime/Data' | 'Electricity' | 'Cable TV' | 'Internet';
const CATEGORIES = [{
  id: 'Airtime/Data',
  icon: Smartphone,
  label: 'Airtime & Data'
}, {
  id: 'Electricity',
  icon: Zap,
  label: 'Electricity'
}, {
  id: 'Cable TV',
  icon: Tv,
  label: 'Cable TV'
}, {
  id: 'Internet',
  icon: Wifi,
  label: 'Internet'
}] as const;
const PROVIDERS = {
  'Airtime/Data': ['MTN', 'Airtel', 'Glo', '9mobile'],
  Electricity: ['Ikeja Electric', 'Eko Electric', 'Abuja Disco', 'Kano Disco'],
  'Cable TV': ['DSTV', 'GOTV', 'Startimes'],
  Internet: ['Spectranet', 'Smile', 'FibreOne']
};
const PACKAGES = {
  DSTV: [{
    name: 'DSTV Premium',
    price: 29500
  }, {
    name: 'DSTV Compact Plus',
    price: 19800
  }, {
    name: 'DSTV Compact',
    price: 12500
  }, {
    name: 'DSTV Confam',
    price: 7400
  }],
  GOTV: [{
    name: 'GOTV Supa+',
    price: 12500
  }, {
    name: 'GOTV Supa',
    price: 7600
  }, {
    name: 'GOTV Max',
    price: 5300
  }]
};
export const BillPayment: React.FC = () => {
  const {
    state,
    processTransaction
  } = useAppContext();
  const [activeTab, setActiveTab] = useState<'pay' | 'saved' | 'history'>('pay');
  const [category, setCategory] = useState<Category>('Airtime/Data');
  // Form State
  const [provider, setProvider] = useState(PROVIDERS['Airtime/Data'][0]);
  const [accountNumber, setAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [meterType, setMeterType] = useState('Prepaid');
  // UI State
  const [isPinOpen, setIsPinOpen] = useState(false);
  const [pin, setPin] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  // Derived
  const billHistory = state.transactions.filter(t => t.type === 'Bills');
  // Reset form when category changes
  const handleCategoryChange = (cat: Category) => {
    setCategory(cat);
    setProvider(PROVIDERS[cat][0]);
    setAccountNumber('');
    setAmount('');
    setSelectedPackage('');
  };
  const handlePackageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const pkgName = e.target.value;
    setSelectedPackage(pkgName);
    // Find price and set amount
    const allPackages = [...(PACKAGES['DSTV'] || []), ...(PACKAGES['GOTV'] || [])];
    const pkg = allPackages.find(p => p.name === pkgName);
    if (pkg) {
      setAmount(pkg.price.toString());
    }
  };
  const handlePaySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountNumber || !amount) {
      toast.error('Please fill all required fields');
      return;
    }
    if (Number(amount) > (state.user?.walletBalance || 0)) {
      toast.error('Insufficient wallet balance');
      return;
    }
    setIsPinOpen(true);
  };
  const confirmPayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== 4) {
      toast.error('PIN must be 4 digits');
      return;
    }
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsPinOpen(false);
      setPin('');
      const title = category === 'Cable TV' && selectedPackage ? selectedPackage : `${provider} ${category === 'Airtime/Data' ? 'Airtime' : category}`;
      const ref = processTransaction(-Number(amount), 'Bills', title);
      toast.success('Payment Successful!', {
        description: `${formatCurrency(Number(amount))} paid for ${title}. Ref: ${ref}`,
        icon: <CheckCircle2 className="text-brand-primary" data-id="element-333" />
      });
      // Reset form
      setAccountNumber('');
      setAmount('');
      setSelectedPackage('');
    }, 1500);
  };
  return <div className="space-y-6" data-id="element-334">
      {/* Header */}
      <div data-id="element-335">
        <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-336">
          Bill Payments
        </h1>
        <p className="text-brand-text-light text-sm" data-id="element-337">
          Pay utilities, buy airtime, and renew subscriptions instantly.
        </p>
      </div>

      <div className="flex space-x-1 bg-white p-1 rounded-xl border border-gray-100 inline-flex" data-id="element-338">
        <button onClick={() => setActiveTab('pay')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'pay' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-339">
          <Receipt size={16} data-id="element-340" /> Pay Bills
        </button>
        <button onClick={() => setActiveTab('saved')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'saved' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-341">
          <Bookmark size={16} data-id="element-342" /> Saved Billers
        </button>
        <button onClick={() => setActiveTab('history')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${activeTab === 'history' ? 'bg-brand-primary/10 text-brand-primary-dark' : 'text-brand-text-light hover:bg-gray-50'}`} data-id="element-343">
          <History size={16} data-id="element-344" /> History
        </button>
      </div>

      {activeTab === 'pay' && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-345">
          {/* Categories Grid */}
          <div className="lg:col-span-1 space-y-3" data-id="element-346">
            <h3 className="font-semibold text-brand-text-dark mb-4" data-id="element-347">
              Select Category
            </h3>
            {CATEGORIES.map(cat => <button key={cat.id} onClick={() => handleCategoryChange(cat.id)} className={`w-full flex items-center gap-4 p-4 rounded-2xl border transition-all ${category === cat.id ? 'border-brand-primary bg-brand-primary/5 text-brand-primary-dark shadow-sm' : 'border-gray-100 bg-white text-gray-600 hover:border-brand-primary/30 hover:bg-gray-50'}`} data-id="element-348">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${category === cat.id ? 'bg-brand-primary text-white' : 'bg-gray-100 text-gray-500'}`} data-id="element-349">
                  <cat.icon size={20} data-id="element-350" />
                </div>
                <span className="font-medium" data-id="element-351">{cat.label}</span>
              </button>)}
          </div>

          {/* Dynamic Form */}
          <div className="lg:col-span-2 bg-white rounded-3xl border border-gray-100 shadow-sm p-6 sm:p-8" data-id="element-352">
            <h3 className="font-semibold text-brand-text-dark mb-6 border-b border-gray-100 pb-4 flex items-center gap-2" data-id="element-353">
              {category} Details
            </h3>

            <form onSubmit={handlePaySubmit} className="space-y-6" data-id="element-354">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-355">
                <div data-id="element-356">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-357">
                    Provider
                  </label>
                  <select value={provider} onChange={e => setProvider(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-358">
                    {PROVIDERS[category].map(p => <option key={p} value={p} data-id="element-359">
                        {p}
                      </option>)}
                  </select>
                </div>

                {category === 'Electricity' && <div data-id="element-360">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-361">
                      Meter Type
                    </label>
                    <select value={meterType} onChange={e => setMeterType(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-362">
                      <option data-id="element-363">Prepaid</option>
                      <option data-id="element-364">Postpaid</option>
                    </select>
                  </div>}

                <div className={category === 'Electricity' ? 'md:col-span-2' : ''} data-id="element-365">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-366">
                    {category === 'Airtime/Data' ? 'Phone Number' : category === 'Electricity' ? 'Meter Number' : category === 'Cable TV' ? 'Smartcard Number' : 'Account Number'}
                  </label>
                  <input type="text" required value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" placeholder={`Enter ${category === 'Airtime/Data' ? 'phone' : 'account'} number`} data-id="element-367" />
                </div>

                {category === 'Cable TV' && (provider === 'DSTV' || provider === 'GOTV') && <div className="md:col-span-2" data-id="element-368">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-369">
                        Select Package
                      </label>
                      <select required value={selectedPackage} onChange={handlePackageChange} className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-370">
                        <option value="" disabled data-id="element-371">
                          Choose a package
                        </option>
                        {PACKAGES[provider as keyof typeof PACKAGES]?.map(pkg => <option key={pkg.name} value={pkg.name} data-id="element-372">
                              {pkg.name} - {formatCurrency(pkg.price)}
                            </option>)}
                      </select>
                    </div>}

                <div className="md:col-span-2" data-id="element-373">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-374">
                    Amount (₦)
                  </label>
                  <input type="number" required min="100" value={amount} onChange={e => setAmount(e.target.value)} readOnly={category === 'Cable TV' && !!selectedPackage} className={`w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-lg font-semibold ${category === 'Cable TV' && selectedPackage ? 'bg-gray-50 text-gray-600' : ''}`} placeholder="0.00" data-id="element-375" />

                  {category === 'Airtime/Data' && <div className="flex flex-wrap gap-2 mt-3" data-id="element-376">
                      {[100, 200, 500, 1000, 2000, 5000].map(amt => <button key={amt} type="button" onClick={() => setAmount(amt.toString())} className="px-4 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:border-brand-primary hover:text-brand-primary transition-colors bg-white" data-id="element-377">
                          ₦{amt}
                        </button>)}
                    </div>}
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex justify-between items-center mt-6" data-id="element-378">
                <div data-id="element-379">
                  <p className="text-sm text-brand-text-light" data-id="element-380">
                    Wallet Balance
                  </p>
                  <p className="font-semibold text-brand-text-dark" data-id="element-381">
                    {formatCurrency(state.user?.walletBalance || 0)}
                  </p>
                </div>
                <button type="submit" disabled={!accountNumber || !amount} className="bg-brand-primary hover:bg-brand-primary-dark text-white px-8 py-3.5 rounded-xl font-semibold transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-382">
                  Pay {amount ? formatCurrency(Number(amount)) : 'Bill'}
                </button>
              </div>
            </form>
          </div>
        </div>}

      {activeTab === 'saved' && <EmptyState icon={Bookmark} title="No saved billers" description="Save your frequent billers for faster payments next time." data-id="element-383" />}

      {activeTab === 'history' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-384">
          {billHistory.length > 0 ? <div className="overflow-x-auto" data-id="element-385">
              <table className="w-full text-left text-sm" data-id="element-386">
                <thead className="bg-gray-50 text-brand-text-light border-b border-gray-100" data-id="element-387">
                  <tr data-id="element-388">
                    <th className="px-6 py-4 font-medium" data-id="element-389">Description</th>
                    <th className="px-6 py-4 font-medium" data-id="element-390">Date</th>
                    <th className="px-6 py-4 font-medium" data-id="element-391">Reference</th>
                    <th className="px-6 py-4 font-medium text-right" data-id="element-392">Amount</th>
                    <th className="px-6 py-4 font-medium text-center" data-id="element-393">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-394">
                  {billHistory.map(tx => <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-395">
                      <td className="px-6 py-4 font-medium text-brand-text-dark" data-id="element-396">
                        {tx.title}
                      </td>
                      <td className="px-6 py-4 text-brand-text-light" data-id="element-397">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-6 py-4 text-brand-text-light font-mono text-xs" data-id="element-398">
                        {tx.reference}
                      </td>
                      <td className="px-6 py-4 font-bold text-brand-text-dark text-right" data-id="element-399">
                        {formatCurrency(Math.abs(tx.amount))}
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-400">
                        <StatusBadge status={tx.status} data-id="element-401" />
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div> : <div className="p-8" data-id="element-402">
              <EmptyState icon={History} title="No payment history" description="Your bill payments will appear here." data-id="element-403" />
            </div>}
        </div>}

      {/* PIN Modal */}
      <Modal isOpen={isPinOpen} onClose={() => setIsPinOpen(false)} title="Confirm Payment" data-id="element-404">
        <form onSubmit={confirmPayment} className="space-y-6" data-id="element-405">
          <div className="text-center" data-id="element-406">
            <p className="text-brand-text-light mb-2" data-id="element-407">You are about to pay</p>
            <p className="text-3xl font-bold text-brand-text-dark" data-id="element-408">
              {formatCurrency(Number(amount))}
            </p>
            <p className="text-sm font-medium text-brand-text-dark mt-2" data-id="element-409">
              for {provider} {category}
            </p>
          </div>

          <div data-id="element-410">
            <label className="block text-sm font-medium text-gray-700 mb-2 text-center" data-id="element-411">
              Enter 4-Digit PIN
            </label>
            <div className="flex justify-center" data-id="element-412">
              <input type="password" maxLength={4} required value={pin} onChange={e => setPin(e.target.value.replace(/\D/g, ''))} className="w-32 text-center tracking-[1em] px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-2xl font-bold" placeholder="••••" data-id="element-413" />
            </div>
          </div>

          <button type="submit" disabled={isProcessing || pin.length !== 4} className="w-full bg-brand-primary hover:bg-brand-primary-dark text-white py-3.5 rounded-xl font-semibold transition-colors disabled:opacity-70 flex items-center justify-center" data-id="element-414">
            {isProcessing ? 'Processing...' : 'Confirm Payment'}
          </button>
        </form>
      </Modal>
    </div>;
};